import React from 'react'
import { routes } from '@sketch/modules-common'
import styled, { css } from 'styled-components'
import copy from 'copy-to-clipboard'
import { useToast } from '@sketch/toasts'
import {
  Box,
  Truncate,
  FlatButton,
  Link,
  Tooltip,
  useModalContext,
} from '@sketch/components'
import { LabelStyled } from './PartialShareModal.styles'
import { useRefreshPreviewMutation } from '@sketch/gql-types'
import { usePreviewForm } from './usePreviewForm'
import { ModalConfirmation } from './ModalConfirmation'
import { ReactComponent as Open } from '@sketch/icons/arrow-right-circle-semifilled-17'
import { ReactComponent as Rectangles } from '@sketch/icons/rectangles-2-17'
import { ReactComponent as Refresh } from '@sketch/icons/arrow-counterclockwise-17'

const PreviewBox = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.foreground.secondary.D};
    background-color: ${theme.colors.background.secondary.B};
    border: 1px dashed ${theme.colors.border.A};
    border-radius: ${theme.radii.large};
    padding: 8px 12px;
    margin-top: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 40px;
    width: 100%;
  `
)

const iconStyles = css`
  width: 17px;
  height: 17px;
  opacity: 0.8;
`

const OpenIcon = styled(Open)`
  ${iconStyles}
`
const CopyIcon = styled(Rectangles)`
  ${iconStyles}
`
const RefreshIcon = styled(Refresh)`
  ${iconStyles}
`

const ActionButton = styled(FlatButton)`
  ${iconStyles};
  padding: 0;
`

const ActionLink = styled(Link)(
  ({ theme }) => css`
    color: ${theme.colors.foreground.secondary.D};
    padding: 0;

    &:hover {
      color: ${theme.colors.foreground.secondary.D};
    }
  `
)

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  margin-top: 4px;
  justify-content: space-between;
  gap: 24px;
  margin-right: 4px;
`

export const PreviewLinkSection = ({
  reopenPartialSharingModal,
}: {
  reopenPartialSharingModal?: (identifier?: string) => void
}) => {
  const { showToast } = useToast()
  const { showModal } = useModalContext()
  const { setPreviewIdentifier, previewIdentifier } = usePreviewForm()

  const [refreshURL, { loading }] = useRefreshPreviewMutation({
    onCompleted: ({ refreshPreview }) => {
      showToast('Preview URL refreshed successfully')
      const newIdentifier = refreshPreview.preview.identifier
      setPreviewIdentifier?.(newIdentifier)
      reopenPartialSharingModal?.(newIdentifier)
    },
    onError: 'show-toast',
  })

  if (!previewIdentifier) {
    return <></>
  }

  const previewURL = routes.PREVIEW_VIEW.create({
    previewID: previewIdentifier || '',
  })

  const url = `${window.location.origin}${previewURL}`

  const handleOnCopy = () => {
    copy(url)
    showToast('Preview link copied')
  }

  const handleOnRefresh = () => {
    if (!previewIdentifier) {
      return null
    }

    showModal(ModalConfirmation, {
      title: 'Generate New Preview Link?',
      description:
        'Anyone with the existing link will no longer have access to this preview.',
      confirmText: 'Generate New Link',
      onConfirm: () => {
        refreshURL({ variables: { identifier: previewIdentifier } })
      },
      didCancel: () => {
        // Reopen the modal when a user cancels removing a preview
        reopenPartialSharingModal?.()
      },
      variant: 'primary',
      loading,
    })
  }

  return (
    <Box mb={4}>
      <LabelStyled>Preview Link</LabelStyled>
      <PreviewBox>
        <Box maxWidth={440}>
          <Truncate>{url}</Truncate>
        </Box>
        <ActionsWrapper>
          <Tooltip content="Generate new link" placement="bottom">
            <ActionButton onClick={handleOnRefresh}>
              <RefreshIcon />
            </ActionButton>
          </Tooltip>
          <Tooltip content="Copy link" placement="bottom">
            <ActionButton>
              <CopyIcon onClick={handleOnCopy} />
            </ActionButton>
          </Tooltip>
          <Tooltip content="Open Preview" placement="bottom">
            <ActionLink to={previewURL} target="_blank">
              <OpenIcon />
            </ActionLink>
          </Tooltip>
        </ActionsWrapper>
      </PreviewBox>
    </Box>
  )
}
